import {useSchedulersHandling} from "../../hooks/schedulers/useSchedulersHandling";
import Layout from "../../components/Layout";
import {Box, capitalize, DialogActions, DialogContent, Paper, Typography} from "@mui/material";
import {schedulersStyles} from "../../styles/schedulers/schedulers.styles";
import {Form, Formik, FormikProps} from "formik";
import {Scheduler} from "../../api";
import FieldsContentScheduler from "../../components/schedulers/FieldsContentScheduler";
import SubmitButton from "../../components/SubmitButton";
import {useSchedulerUpdateForm} from "../../hooks/schedulers/useSchedulerUpdateForm";
import {useScheduler} from "../../client/queries";
import {useParams} from "react-router";
import ErrorAlert from "../../components/ErrorAlert";
import {notPermitted} from "../../errors";
import MultipleGuard from "../../components/guards/MultipleGuard";
import {permissions} from "../../permissions";
import ButtonDeleteScheduler from "../../components/schedulers/ButtonDeleteScheduler";
import type {SchedulerFormValues} from "../../types/schedulerTypes";

export default function PageUpdateScheduler() {
    const {schedulerId = ""} = useParams<{ schedulerId: string; }>();
    const {translation, canEditScheduler, canDeleteScheduler} = useSchedulersHandling();
    const scheduler: Scheduler = useScheduler(schedulerId);
    const formikProps = useSchedulerUpdateForm(scheduler);
    const canEdit = canEditScheduler(scheduler);
    const canDelete = canDeleteScheduler(scheduler);

    return (
        <Layout title={capitalize(translation('scheduler-edit'))} back={"/schedulers"}>
            <MultipleGuard
                permissions={[
                    permissions.schedulers.edit,
                    permissions.schedulers.own_edit,
                    permissions.schedulers.show,
                    permissions.schedulers.own_show,
                ]}
                fallback={<ErrorAlert error={notPermitted()}/>}
            >
                <Paper id={'page-update-scheduler-paper'}
                       elevation={1}
                       sx={schedulersStyles.pageUpdateScheduler.paper}
                >
                    <Formik {...formikProps}>
                        {({isSubmitting, isValidating, isValid, dirty}: FormikProps<SchedulerFormValues>) => (
                            <Form id={'form-update-scheduler'}>
                                <Box sx={schedulersStyles.pageUpdateScheduler.headerBoxWrapper}>
                                    <Typography variant="h5">
                                        {capitalize(translation('scheduler-edit'))}
                                    </Typography>
                                    {canDelete && (
                                        <ButtonDeleteScheduler
                                            schedulerId={schedulerId}
                                            disabled={(scheduler.usedByAufgabenCount ?? 0) > 0}
                                            disabledReason={(scheduler.usedByAufgabenCount ?? 0) > 0
                                                ? translation('scheduler-delete-disabled-in-use', {count: scheduler.usedByAufgabenCount})
                                                : undefined}
                                        />
                                    )}
                                </Box>
                                <DialogContent id={'page-update-scheduler-dialog-content'}>
                                    <FieldsContentScheduler canEditFields={canEdit}/>
                                </DialogContent>
                                <DialogActions id={'page-update-scheduler-dialog-actions'}>
                                    <SubmitButton id={'page-update-scheduler-submit-button'}
                                                  form={'form-update-scheduler'}
                                                  type={'submit'}
                                                  variant={'contained'}
                                                  color={'secondary'}
                                                  loading={isSubmitting}
                                                  disabled={isValidating || !isValid || !dirty || !canEdit}
                                    >
                                        {isSubmitting ? capitalize(translation('updating')) : capitalize(translation('update'))}
                                    </SubmitButton>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </Paper>
            </MultipleGuard>
        </Layout>
    );
}
